import React from "react";
import PropTypes from "prop-types";
import { get, chunk } from "lodash";
import { LazyCollection, wrapCollectionLayout, LoadMoreCollectionStories } from "@quintype/components";

import { addAdsToTemplates } from "../../get-collection-template";
import BreadCrumb from "../story/story-templates/shared/breadcrumbs";
import FiveStoryOneAdOneWidget from "../../rows/five-story-one-ad-one-widget";
import NineteenStoriesOneAd from "../../rows/nineteen-stories-one-ad";
import SecondaryMenu from "../../atoms/secondary-menu";
import { LoadMoreButton } from "../../atoms/read-more-link";
import CollectionBannerImage from "../../atoms/collection-banner";
import { GA, Lotame, WebEngage, ComScore, DeepBI } from "../../../integrations";
import styles from "./styles.m.css";
import { DfpAd } from "../../atoms/dfp-ad";
const LOAD_MORE_TEXT = "மேலும் படிக்க";

const newTemplates = addAdsToTemplates("section");
function getAdsWrappedCollectionTemplate(designTemplate) {
  return newTemplates[designTemplate] || newTemplates.defaultTemplate;
}

const wrapForLoadMore = (Component, numberOfStoriesItCanAccomodate) => {
  const WrapperForLoadMore = ({ stories, collection, onLoadMore, noMoreStories, loading }) => {
    return (
      <React.Fragment>
        {chunk(stories.slice(5), numberOfStoriesItCanAccomodate).map((stories, index) => {
          const items = stories.map(story => {
            return { id: story.id, type: "story", story };
          });
          return <Component key={index} collection={{ ...collection, items }} pagetype="section" />;
        })}
        {!noMoreStories &&
          stories.length > numberOfStoriesItCanAccomodate && (
            <div className={styles["load-more-button-wrapper"]}>
              <LoadMoreButton onClick={onLoadMore} disabled={loading} text={LOAD_MORE_TEXT} />
            </div>
          )}
        <DfpAd className="hidden-mobile" adtype="Leaderboard" placement="section" />
      </React.Fragment>
    );
  };
  WrapperForLoadMore.propTypes = {
    stories: PropTypes.array,
    collection: PropTypes.object,
    onLoadMore: PropTypes.func,
    noMoreStories: PropTypes.func,
    loading: PropTypes.bool
  };
  return WrapperForLoadMore;
};

function shouldShowCollectionLayout(collection) {
  const firstCollection = get(collection, ["items"], []).find(item => item.type === "collection");
  return !!firstCollection;
}

export class SectionPage extends React.Component {
  componentDidMount() {
    const sections = [this.props.data.section];
    if (this.props.data.parentSection) {
      sections.unshift(this.props.data.parentSection);
    }
    GA.registerPageView(this.props.pageType, { section: this.props.data.section });
    Lotame.registerPageView({ sections });
    WebEngage.registerPageView({ section: this.props.data.section });
    ComScore.registerPageView();
    DeepBI.registerPageView({ section: this.props.data.section });
  }

  render() {
    const stories = get(this.props.data, ["collection", "items"], []);

    const sectionSlug = get(this.props.data, ["collection", "slug"], "");
    const section = get(this.props.data, ["section"], {});
    const sectionName = get(this.props.data, ["section", "display-name"], "");
    const collectionName = get(this.props.data, ["collection", "name"], "");
    const sectionMenu = get(this.props.data, ["menuData"], null);
    const sectionColor = get(section, ["data", "color"]);

    if (stories.length < 1) {
      return (
        <div className={styles["default-section-page"]}>
          <div className={styles["no-stories"]}>
            <h2 className={styles["no-stories-status"]}>No stories found.</h2>
          </div>
        </div>
      );
    }
    if (shouldShowCollectionLayout(this.props.data.collection)) {
      const collectionItems = get(this.props.data, ["collection", "items"], []).filter(
        items => items.type === "collection"
      );
      const collection = { ...this.props.data.collection, items: [...collectionItems] };
      return (
        <div className={`${styles["default-section-page"]} ${styles["default-section-page-container"]}`}>
          {sectionMenu ? (
            <SecondaryMenu
              communityColor={sectionMenu["menuBgColor"] || sectionColor}
              menuGroup={sectionMenu}
              slug={sectionMenu["slug"] || sectionSlug}
            />
          ) : null}
          <div className={"container"}>
            {!Object.keys(section).length && Object.keys(collection).length ? (
              <BreadCrumb config={this.props.config} collection={collection} />
            ) : (
              <BreadCrumb config={this.props.config} subSection={section} />
            )}
          </div>
          {sectionName || collectionName ? (
            <div className="container">
              <h1 className={styles["headline"]}>{sectionName || collectionName}</h1>
            </div>
          ) : null}
          <LazyCollection
            collection={collection}
            collectionTemplates={getAdsWrappedCollectionTemplate}
            lazyAfter={10}
            accentColor={sectionColor}
          />
        </div>
      );
    }
    const initialStories = get(this.props.data, ["collection", "items"], []).filter(items => items.type === "story");
    const WrappedFiveStoryOneAdOneWidget = wrapCollectionLayout(FiveStoryOneAdOneWidget);
    return (
      <div className={`main-page ${styles["default-section-page-container"]}`}>
        <DfpAd className={`hidden-mobile ad-tower-left`} adtype="Tower_Left" placement="all" />
        <div className={styles["default-section-page"]}>
          {sectionMenu ? (
            <SecondaryMenu
              communityColor={sectionMenu["menuBgColor"] || sectionColor}
              menuGroup={sectionMenu}
              slug={sectionMenu["slug"] || sectionSlug}
            />
          ) : null}
          <div className={"container"}>
            {this.props.data.section ? (
              <BreadCrumb config={this.props.config} subSection={this.props.data.section} />
            ) : (
              <BreadCrumb config={this.props.config} collection={this.props.data.collection} />
            )}
          </div>
          {sectionSlug === "wedding-season-2021" ? (
            <CollectionBannerImage collection={this.props.data.collection} className={styles["collection-item"]} />
          ) : sectionName || collectionName ? (
            <div className="container">
              <h1 className={styles["headline"]}>{sectionName || collectionName}</h1>
            </div>
          ) : null}
          <WrappedFiveStoryOneAdOneWidget
            showReadmore={false}
            accentColor={sectionColor}
            collection={{ ...this.props.data.collection, items: initialStories.slice(0, 5) }}
            pagetype="section"
          />
          <LoadMoreCollectionStories
            template={wrapForLoadMore(NineteenStoriesOneAd, 19)}
            collectionSlug={this.props.data.collection.slug}
            data={Object.assign(
              {},
              { collection: this.props.data.collection },
              { stories: [...initialStories.map(({ story }) => story).slice(0, 24)] },
              { storiesPerPage: 19 }
            )}
            numStoriesToLoad={19}
            params={{ "item-type": "story" }}
          />
          <DfpAd className="hidden-desktop" adtype="Mobile_lightbox" placement="section" />
          <DfpAd className="hidden-mobile" adtype="Subscription_Lightbox" placement="section" />
        </div>
        <DfpAd className="hidden-mobile ad-tower-right" adtype="Tower_Right" placement="all" />
      </div>
    );
  }
}

SectionPage.propTypes = {
  data: PropTypes.shape({
    collection: PropTypes.object,
    section: PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.shape({
        color: PropTypes.string
      })
    }),
    parentSection: PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.shape({
        color: PropTypes.string
      })
    })
  }),
  config: PropTypes.shape({}),
  pageType: PropTypes.string
};
